import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Textarea } from "../../../../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Checkbox } from "../../../../components/ui/checkbox";
import { useToast } from "../../../../hooks/use-toast";
import { createCoupon, getCouponById, updateCoupon, updateCouponStatus, getCoinPackageTemplates } from '../../../../services/couponAdminApi';
import { DateTimePicker } from '../../../../components/Shared/dateTimePicker';
import { ArrowLeft } from 'lucide-react';

const CouponForm = () => {
  const { couponId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(couponId);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    coupon_type: 'PERCENT_DISCOUNT',
    value: '',
    percentage: '',
    start_date: null,
    expiry_date: null,
    relative_expiry_days: '',
    min_seller_age_days: '',
    max_redemptions_total: '',
    max_redemptions_per_user: '1',
    applicability_type: 'ALL_SELLERS',
    target_is_paid_seller: 'any',
    applicable_package_ids: new Set(),
    raw_conditions: '{}',
  });

  const [originalStatus, setOriginalStatus] = useState(null);
  const [groupedPackages, setGroupedPackages] = useState({ FREE: [], PAID: [] });

  useEffect(() => {
    getCoinPackageTemplates()
      .then(res => {
        const filteredFreePackages = (res.data.FREE || []);
        setGroupedPackages({
            FREE: filteredFreePackages,
            PAID: res.data.PAID || []
        });
      })
      .catch(err => console.error("Failed to fetch coin packages", err))

    if (isEditing) {
      getCouponById(couponId).then(res => {
        const { data } = res;
        const conditions = data.conditions || {};

        setOriginalStatus(data.status);

        let paidStatus = 'any';
        if (conditions.target_is_paid_seller === true) paidStatus = 'yes';
        if (conditions.target_is_paid_seller === false) paidStatus = 'no';

        setFormData({
            code: data.code || '',
            name: data.name || '',
            description: data.description || '',
            coupon_type: data.coupon_type || 'PERCENT_DISCOUNT',
            value: data.value || '',
            percentage: data.percentage || '',
            start_date: data.start_date ? new Date(data.start_date) : null,
            expiry_date: data.expiry_date ? new Date(data.expiry_date) : null,
            relative_expiry_days: data.relative_expiry_days || '',
            min_seller_age_days: data.min_seller_age_days || '',
            max_redemptions_total: data.max_redemptions_total || '',
            max_redemptions_per_user: data.max_redemptions_per_user || '1',
            applicability_type: data.applicability_type || 'ALL_SELLERS',
            target_is_paid_seller: paidStatus,
            applicable_package_ids: new Set(conditions.applicable_package_ids || []),
            raw_conditions: JSON.stringify(conditions, null, 2),
        });
      });
    }
  }, [couponId, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
     setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };

  const handlePackageToggle = (packageId) => {
    setFormData(prev => {
      const newSet = new Set(prev.applicable_package_ids);
      if (newSet.has(packageId)) {
        newSet.delete(packageId);
      } else {
        newSet.add(packageId);
      }
      return { ...prev, applicable_package_ids: newSet };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        coupon_type: formData.coupon_type,
        applicability_type: formData.applicability_type,
        max_redemptions_per_user: formData.max_redemptions_per_user,
    };

    if (formData.percentage && formData.coupon_type === 'PERCENT_DISCOUNT') payload.percentage = formData.percentage;
    if (formData.value && formData.coupon_type !== 'PERCENT_DISCOUNT') payload.value = formData.value;
    if (formData.start_date) payload.start_date = formData.start_date.toISOString();
    if (formData.expiry_date) payload.expiry_date = formData.expiry_date.toISOString();
    if (formData.relative_expiry_days) payload.relative_expiry_days = formData.relative_expiry_days;
    if (formData.min_seller_age_days) payload.min_seller_age_days = formData.min_seller_age_days;
    if (formData.max_redemptions_total) payload.max_redemptions_total = formData.max_redemptions_total;

    let conditions = {};
    try {
      conditions = JSON.parse(formData.raw_conditions);
    } catch (err) {
      toast({ variant: "destructive", title: "Invalid JSON", description: "The Raw JSON Conditions field contains invalid JSON."});
      return;
    }

    if (formData.target_is_paid_seller === 'yes') {
      conditions.target_is_paid_seller = true;
    } else if (formData.target_is_paid_seller === 'no') {
      conditions.target_is_paid_seller = false;
    } else {
      delete conditions.target_is_paid_seller;
    }

    if (formData.applicable_package_ids.size > 0) {
      conditions.applicable_package_ids = Array.from(formData.applicable_package_ids);
    } else {
      delete conditions.applicable_package_ids;
    }
    payload.conditions = conditions;

    const apiCall = isEditing ? updateCoupon(couponId, payload) : createCoupon(payload);

    apiCall.then(() => {
      if (isEditing && originalStatus === 'EXPIRED') {
        return updateCouponStatus(couponId, 'DRAFT');
      }
    }).then(() => {
      toast({ title: "Success", description: `Coupon successfully ${isEditing ? 'updated' : 'created'}.` });
      navigate('/admin/payments/view/draft');
    }).catch(err => {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Operation failed: ${err.response?.data?.detail || err.message}`,
      });
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-xl font-semibold">{isEditing ? 'Edit Coupon' : 'Create New Coupon'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Core Details</CardTitle>
            <CardDescription>Basic information for the coupon.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label htmlFor="code">Coupon Code</Label><Input id="code" name="code" value={formData.code} onChange={handleChange} required /></div>
              <div><Label htmlFor="name">Coupon Name (for Admin)</Label><Input id="name" name="name" value={formData.name} onChange={handleChange} required /></div>
            </div>
            <div><Label htmlFor="description">Description (for Seller)</Label><Textarea id="description" name="description" value={formData.description} onChange={handleChange} required /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coupon Effect</CardTitle>
            <CardDescription>What benefit does this coupon provide? Choose one primary effect.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Coupon Type</Label><Select name="coupon_type" value={formData.coupon_type} onValueChange={(val) => handleSelectChange('coupon_type', val)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="PERCENT_DISCOUNT">Percentage Discount</SelectItem><SelectItem value="VALUE_DISCOUNT">Fixed Value Discount</SelectItem><SelectItem value="ADDITIONAL_COINS_PERCENT">Percentage Additional Coins</SelectItem><SelectItem value="ADDITIONAL_COINS_ABSOLUTE">Absolute Additional Coins</SelectItem><SelectItem value="FREE_RESPONSES">Add Free Responses</SelectItem></SelectContent></Select></div>
            {['PERCENT_DISCOUNT', 'ADDITIONAL_COINS_PERCENT'].includes(formData.coupon_type) && (<div><Label htmlFor="percentage">Percentage</Label><Input id="percentage" name="percentage" type="number" step="0.01" value={formData.percentage} onChange={handleChange} placeholder="e.g., 15.5 for 15.5%"/></div>)}
            {['VALUE_DISCOUNT', 'ADDITIONAL_COINS_ABSOLUTE', 'FREE_RESPONSES'].includes(formData.coupon_type) && (<div><Label htmlFor="value">Value (Amount, Coins, or # of Responses)</Label><Input id="value" name="value" type="number" value={formData.value} onChange={handleChange} placeholder="e.g., 500 for ₹500 or 100 for 100 coins" /></div>)}
          </CardContent>
        </Card>

        <Card>
            <CardHeader>
                <CardTitle>Applicability & Targeting</CardTitle>
                <CardDescription>Define who can use this coupon and on what products.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Coupon Applicability</Label><Select name="applicability_type" value={formData.applicability_type} onValueChange={(val) => handleSelectChange('applicability_type', val)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL_SELLERS">All Sellers</SelectItem><SelectItem value="SPECIFIC_SELLERS">Specific Sellers Only (via activation)</SelectItem></SelectContent></Select></div>
                  <div><Label>Target Seller Status</Label><Select name="target_is_paid_seller" value={formData.target_is_paid_seller} onValueChange={(val) => handleSelectChange('target_is_paid_seller', val)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="any">Any Seller</SelectItem><SelectItem value="yes">Only Paid Sellers</SelectItem><SelectItem value="no">Only Non-Paid Sellers</SelectItem></SelectContent></Select></div>
                </div>
                <div>
                  <Label htmlFor="min_seller_age_days">Minimum Seller Account Age (days)</Label>
                  <Input
                    id="min_seller_age_days"
                    name="min_seller_age_days"
                    type="number"
                    min="0"
                    value={formData.min_seller_age_days}
                    onChange={handleChange}
                    placeholder="e.g., 30. Leave blank for no age restriction."
                  />
                </div>
                {formData.coupon_type !== 'FREE_RESPONSES' && <div>
                    <Label>Applicable Coin Packages</Label>
                    <p className="text-sm text-muted-foreground">If none are selected, the coupon applies to all packages.</p>
                    {(formData.target_is_paid_seller === 'no' || formData.target_is_paid_seller === 'any') && <div className="mt-4">
                        <h4 className="font-semibold text-md mb-2">Packages for FREE Tier</h4>
                        <div className="rounded-md border p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                            {groupedPackages.FREE.map(pkg => (
                                <div key={pkg.id} className="flex items-center space-x-2">
                                    <Checkbox id={`pkg-${pkg.id}`} checked={formData.applicable_package_ids.has(pkg.id)} onCheckedChange={() => handlePackageToggle(pkg.id)} />
                                    <Label htmlFor={`pkg-${pkg.id}`} className="font-normal">{pkg.name}</Label>
                                </div>
                            ))}
                        </div>
                    </div>}

                    {(formData.target_is_paid_seller === 'yes' || formData.target_is_paid_seller === 'any') && <div className="mt-4">
                        <h4 className="font-semibold text-md mb-2">Packages for PAID Tier</h4>
                        <div className="rounded-md border p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                            {groupedPackages.PAID.map(pkg => (
                                <div key={pkg.id} className="flex items-center space-x-2">
                                    <Checkbox id={`pkg-${pkg.id}`} checked={formData.applicable_package_ids.has(pkg.id)} onCheckedChange={() => handlePackageToggle(pkg.id)} />
                                    <Label htmlFor={`pkg-${pkg.id}`} className="font-normal">{pkg.name}</Label>
                                </div>
                            ))}
                        </div>
                    </div>}
                </div>}
            </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Usage Limits & Duration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label htmlFor="max_redemptions_total">Max Total Redemptions</Label><Input id="max_redemptions_total" name="max_redemptions_total" type="number" value={formData.max_redemptions_total} onChange={handleChange} placeholder="Leave blank for unlimited"/></div>
              <div><Label htmlFor="max_redemptions_per_user">Max Redemptions Per User</Label><Input id="max_redemptions_per_user" name="max_redemptions_per_user" type="number" value={formData.max_redemptions_per_user} onChange={handleChange} required/></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DateTimePicker
                label="Activation Date & Time (Local)"
                date={formData.start_date}
                setDate={(d) => handleDateChange('start_date', d)}
              />
              <DateTimePicker
                label="Expiry Date & Time (Absolute)"
                date={formData.expiry_date}
                setDate={(d) => handleDateChange('expiry_date', d)}
                disabled={!!formData.relative_expiry_days}
              />
            </div>
            <div>
              <Label htmlFor="relative_expiry_days">Relative Expiry (days after activation)</Label>
              <Input
                id="relative_expiry_days"
                name="relative_expiry_days"
                type="number"
                value={formData.relative_expiry_days}
                onChange={(e) => setFormData(prev => ({...prev, relative_expiry_days: e.target.value}))}
                placeholder="e.g., 7. Overrides absolute expiry date."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Advanced Conditions</CardTitle>
            <CardDescription>For developers: Add any other conditions as a raw JSON object. This is a secure alternative to custom SQL.</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea name="raw_conditions" value={formData.raw_conditions} onChange={handleChange} rows={5} placeholder='{ "min_signup_days": 30 }'/>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="button" variant="outline" size="lg" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" size="lg" className="flex-1" variant="default">{isEditing ? 'Save Changes' : 'Create Coupon (as Draft)'}</Button>
        </div>
      </form>
    </div>
  );
};

export default CouponForm;
